import React, { useState, useEffect, useRef } from "react";
import {
  Avatar,
  Box,
  Button,
  getColor,
  Icons,
  InfiniteScrollBox,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { Alarm } from "../../types/common";
import utilsCommon from "../../shared/utils/common";
import classNames from "classnames";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { Workgroup } from "../../types/workgroup";
import { nanoid } from "@reduxjs/toolkit";
import { handleDocDownload, handleDocPreview } from "../../shared/utils/api";
import { useTranslation } from "react-i18next";

const Notification = () => {
  /** hook 정리 */
  const toasts = useToast();
  const { t } = useTranslation();
  const documentListNewCard = useRef([] as any[]);
  const { loginState, getAlarmList, readAlarm, readAllAlarm } = useLoginStore();
  const { getContentDownload } = useDocumentStore();
  const { workgroupState } = useWorkgroupStore();

  /** state 정리 */
  const [key, setKey] = useState(0);

  /** useEffect 정리 */
  useEffect(() => {
    let newDataCardContent = [] as any[];
    loginState.alarmResponse?.alarmResults?.forEach(
      (item: Alarm, index: number) => {
        newDataCardContent.push(getTableCardData(item, index));
      }
    );
    documentListNewCard.current = newDataCardContent;
    setKey(key + 1);
  }, [loginState.alarmResponse]);

  /** function 정리 */
  /* UI:알림 메세지
    Alarm type
      1. invite : 워크그룹 초대
      2. memo : 워크그룹 메모
      2. sign: 문서 의견
      3. share: 문서 공유
  */
  const renderAlarmTitle = (row: Alarm) => {
    switch (row.alarmType) {
      case "invite":
        return t("header.alarm.invite", {
          name: row.senderName,
          workgroupName: row.workgroupName,
        });
      case "sign":
      case "memo":
        return t("header.alarm.shareComment", {
          name: row.senderName,
          comment:
            row.msg.length > 45 ? row.msg.substr(0, 45) + "..." : row.msg,
        });

      case "share":
        return t("header.alarm.shareDoc", {
          name: row.senderName,
          docName: row.contentName,
        });
    }
  };
  /** 워크그룹 알림 클릭시 워크그룹 이동 */
  const handleMoveToWorkgroup = (syncId: string, workgroupId: string) => {
    if (syncId) return;
    let targetWorkgroup = workgroupState.workgroupSidemenuList.filter(
      (workgroup: Workgroup) =>
        workgroup.workgroupId === workgroupId && workgroup.isValidate !== 0
    );
    if (targetWorkgroup.length > 0) {
      window.location.href = `${process.env.PUBLIC_URL}/workgroup/${workgroupId}/home`;
    } else {
      toasts.toastMsg(nanoid(), t("common.alert.noneWorkgroup"), "error");
    }
  };
  /** 미리보기 허용 사이트 아니면 표시 X */
  const isAllowedPrview = (row: Alarm) => {
    return (
      row.syncId &&
      utilsCommon.checkLicenseAvailable("allowWebView") &&
      utilsCommon.checkExtensionAvailable(row.contentName, "fwocExtensions")
    );
  };
  /** UI: 알림 아이템 */
  const getTableCardData = (row: Alarm, index: any) => {
    return {
      id: row?.logId,
      data: (
        <Box
          classes={classNames("notification-item", {
            "cursor pointer": !row.syncId,
          })}
          onClick={() => {
            if (row.syncId) return;
            handleMoveToWorkgroup(row.syncId, row.workgroupId);
            readAlarm({ logId: row.logId });
          }}
        >
          <Box width={6}>
            {row.alarmStatus === "S" && (
              <Icons
                variant="fill"
                label="dot"
                size={6}
                classes="notification-item--dot"
                stroke={getColor("COLOR_BG_ERROR")}
              />
            )}
          </Box>

          <Box
            classes={classNames("notification-item--body", {
              isFile: row.alarmType !== "invite",
            })}
          >
            <Avatar
              uniqueCode={utilsCommon.getUserColor(row.userType)}
              name={row.senderName ?? row.senderId}
              icon={row.senderName ? undefined : "user"}
            />
            <Box
              direction="column"
              classes="mg ml-12"
              width={"calc(100% - 44px)"}
            >
              <Typography wordBreak="break-word" type="body2_rg">
                {renderAlarmTitle(row)}
              </Typography>
              <Box classes="mg mt-6">
                <Tooltip
                  maxWidth={"170px"}
                  message={
                    row.workgroupName === "NONEWORKGROUP"
                      ? "My Box"
                      : row.workgroupName
                  }
                >
                  <Typography
                    type="caption1_rg"
                    exactColor={"#717985"}
                    ellipsis
                  >
                    {row.workgroupName === "NONEWORKGROUP"
                      ? "My Box"
                      : row.workgroupName}
                  </Typography>
                </Tooltip>
                <Typography
                  type="caption1_rg"
                  classes="mg ml-4"
                  exactColor={"#717985"}
                >{` · ${utilsCommon.renderDateFormat(
                  row.logDate
                )}`}</Typography>
              </Box>
              {row.syncId && (
                <Box classes="notification-actions">
                  {isAllowedPrview(row) && (
                    <Tooltip message={t("file.action.preview")}>
                      <Button
                        btnStyle="table-icon"
                        leadingIcon={
                          <Icons variant="line" label="preview" isOn />
                        }
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDocPreview({
                            permission: "view",
                            workgroupId: row?.workgroupId,
                            syncId: row?.syncId,
                            toast: toasts,
                            t,
                            revisionNo: 0,
                          });
                          readAlarm({ logId: row.logId });
                        }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip message={t("common.download")} classes="mg ml-4">
                    <Button
                      btnStyle="table-icon"
                      leadingIcon={<Icons variant="line" label="download" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDocDownload(
                          row.syncId,
                          getContentDownload,
                          toasts,
                          t
                        );
                        readAlarm({ logId: row.logId });
                      }}
                    />
                  </Tooltip>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      ),
    };
  };

  return (
    <Box height={480} classes="notification">
      <Box classes="notification-header">
        <Typography type="h3">{t("header.alarm.title")}</Typography>
        {documentListNewCard.current.length > 0 && (
          <Button
            label={t("header.alarm.readAll")}
            btnStyle="link"
            classes="mg ml-auto"
            leadingIcon={<Icons variant={"line"} label={"check"} />}
            onClick={() => readAllAlarm({})}
          />
        )}
      </Box>
      {documentListNewCard.current.length > 0 ? (
        <InfiniteScrollBox
          height="408px"
          width={"404px"}
          id="notification-scroll"
          data={documentListNewCard.current}
          endReached={() => {
            if (loginState.alarmResponse?.lastLogId !== "-1")
              getAlarmList({
                userId: "",
                order: "",
                logId: loginState.alarmResponse.lastLogId,
              });
          }}
        />
      ) : (
        <Box alignItems="center" direction="column">
          <img
            src={require("../../shared/images/common/alarm.png")}
            alt={"thumbnail"}
            width={200}
            height={120}
            style={{ marginTop: 80 }}
          />
          <Typography
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
            type="body2_rg"
            classes="mg mt-8"
          >
            {t("header.alarm.noAlarm")}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Notification;
